/**
 * Core types, constants, and utilities for the football betting system.
 * Current Year: 2026
 */

export const ROUTE_PATHS = {
  DASHBOARD: "/dashboard",
  LOGIN: "/login",
  USERS: "/users",
  MATCHES: "/matches",
  MATCH_MANAGEMENT: "/match-management",
  MATCH_RESULTS: "/match-results",
  REPORTS: "/reports",
  WALLET: "/wallet",
  CREDIT: "/credit",
  PRE_MATCH_SUMMARY: "/pre-match-summary",
  PRE_MATCH_SUMMARY_2: "/focus-prediction-2",
} as const;

export enum UserTier {
  MASTER = "MASTER",
  COMPANY = "COMPANY",
  AGENT = "AGENT",
  PLAYER = "PLAYER",
}

export enum UserStatus {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  BANNED = "BANNED",
}

export interface User {
  id: string;
  username: string;
  nickname?: string;   // display name shown on receipts; falls back to username if not set
  description?: string; // free-text notes/description for this user
  password?: string;
  tier: UserTier;
  parentId?: string;
  status: UserStatus;
  balance: number;
  creditLimit: number;
  availableCreditLimit?: number;
  ticketPrice: number;
  fightPercentage: number;
  fightPctLimit: number;  // max fight% this user may assign to their direct downline agents
  /** 'single' = uniform fight% for all downlines; 'multiple' = per-downline rates */
  fightPctMode?: 'single' | 'multiple';
  /** What the parent agent charges THIS user (only relevant when parent is in 'multiple' mode) */
  parentFightPct?: number;
  isOverEnabled?: boolean;
  /** Whether the fight % feature is enabled/disabled for this user */
  fightOnOff?: boolean;
  /** How the parent refreshes this user's credit: auto_daily | auto_weekly | manual */
  creditRefreshRate?: 'auto_daily' | 'auto_weekly' | 'manual';
  /** ISO timestamp of the last credit refresh (auto or manual) */
  lastCreditRefresh?: string | null;
  /** true = user has never completed the first-time setup screen */
  firstLogin?: boolean;
  createdAt: string;
}

export enum MatchStatus {
  UPCOMING = "UPCOMING",
  LIVE = "LIVE",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export interface ScoreOdds {
  [key: string]: number;
}

export interface Match {
  id: string;
  matchNumber?: string;  // e.g. "M001", "M002"
  homeTeam: string;
  awayTeam: string;
  odds: {
    home: number;
    away: number;
    draw: number;
  };
  scoreOdds?: ScoreOdds; // New score-based odds
  startTime: string;
  status: MatchStatus;
  result?: string; // Changed to support score format like "2-1", "0-0", "aol"
  companyId?: string;  // Company that owns this match
  createdBy?: string;  // User who created this match
}

export enum BetStatus {
  PENDING = "PENDING",
  WON = "WON",
  LOST = "LOST",
  CANCELLED = "CANCELLED",
}

export interface Bet {
  id: string;
  userId: string;
  matchId: string;
  selection: string; // Changed to support score-based betting like "2-1", "0-0", "aol"
  amount: number;
  oddsAtTime: number;
  status: BetStatus;
  createdAt: string;
  updatedAt?: string;  // timestamp of last status change (e.g. cancellation time)
  ticketPrice: number;
  fightPercentage: number;
  fightPctSnapshot?: number; // fight% snapshotted at bet placement time for the parent agent
  fightPctSnapshotChain?: number; // combined compounded fight% across ALL ancestor levels at bet time
  transactionId?: string;
  remark?: string;
  soldOutSnapshot?: Array<{ selection: string; soldOutAmount: number; requestedAmount: number; acceptedAmount: number; tightestTier: string }>; // stored on first bet of txn
}

export interface WalletTransaction {
  id: string;
  userId: string;
  amount: number;
  type: "in" | "out";
  remark: string;
  createdAt: string;
  performedBy: string;
}

/**
 * Calculates the balance based on the business formula:
 * WIN:  ((total tickets × ticket price) × (odd − 1)) × (100% − fight%)
 * LOSE: −(total tickets × ticket price) × (100% − fight%)
 * @param betAmount      Total tickets placed
 * @param ticketPrice    Ticket price multiplier
 * @param fightPercentage Fight percentage (0-100)
 * @param odds           Odds for the selection
 * @param isWin          Whether the bet won or lost
 */
export const calculateSettlement = (
  betAmount: number,
  ticketPrice: number,
  fightPercentage: number,
  odds: number,
  isWin: boolean
): number => {
  const fightMultiplier = (100 - fightPercentage) / 100;
  if (!isWin) {
    // LOSE: −(total tickets × ticket price) × (100% − fight%)
    return -(betAmount * ticketPrice * fightMultiplier);
  }
  // WIN: ((total tickets × ticket price) × (odd − 1)) × (100% − fight%)
  return (betAmount * ticketPrice * (odds - 1)) * fightMultiplier;
};

/**
 * Calculates display balance for reports.
 * balance = ((total tickets × ticket price) × (odd − 1)) × (100% − fight%)
 */
export const calculateBalance = (
  outcome: number,
  betAmount: number,
  odds: number,
  fightPercentage: number,
  ticketPrice: number
): number => {
  return calculateSettlement(betAmount, ticketPrice, fightPercentage, odds, outcome > 0);
};

/**
 * Formats numbers into RM currency string
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Returns a Date whose local display equals MYT (UTC+8) wall-clock time,
 * regardless of the viewer's browser timezone.
 *
 * date-fns format() always uses the local timezone. To guarantee the printed
 * time is MYT, we shift so that:
 *   local display value == UTC_epoch + 8h  (MYT wall clock)
 *
 * e.g. DB stores 2026-06-11T19:00:00Z → MYT = 03:00 Jun 12.
 * format(toMYT(startTime), 'dd MMM HH:mm') always prints "12 Jun 03:00".
 */
export const toMYT = (date: Date | string): Date => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;
  // getTimezoneOffset() returns minutes BEHIND UTC, e.g. UTC+8 → -480
  const localOffsetMs = d.getTimezoneOffset() * 60 * 1000;
  // Shift so local display == MYT wall-clock
  return new Date(d.getTime() + MYT_OFFSET_MS + localOffsetMs);
};

/**
 * Checks if a bet can be placed based on timing (at least 5 mins before start)
 */
export const canPlaceBet = (startTime: string): boolean => {
  // DB stores UTC times; compare directly with Date.now()
  const matchTimeUTC = new Date(startTime).getTime();
  const currentTime = new Date().getTime();
  const fiveMinutesInMs = 5 * 60 * 1000;
  return matchTimeUTC - currentTime > fiveMinutesInMs;
};

/**
 * Validates fight percentage based on user tier
 * Company: 100% fixed
 * Agent: 0% to (parent fight% - 5%), steps of 5
 * Player: No fight percentage (0%)
 */
export const validateFightPercentage = (
  tier: UserTier,
  percentage: number,
  parentFightPercentageOrLimit?: number
): boolean => {
  switch (tier) {
    case UserTier.MASTER:
      return percentage === 105;
    case UserTier.COMPANY:
      return percentage === 100;
    case UserTier.AGENT: {
      // parentFightPercentageOrLimit is the setter's fightPctLimit (or fightPercentage as fallback)
      const max = parentFightPercentageOrLimit !== undefined
        ? Math.max(0, parentFightPercentageOrLimit - 5)
        : 90;
      return percentage >= 0 && percentage <= max && percentage % 5 === 0;
    }
    case UserTier.PLAYER:
      return percentage === 0;
    default:
      return false;
  }
};

/**
 * Validates ticket price: must be between creator's own ticket price and RM 1.00 (max).
 * Rule applies to both Company and Agent when creating subordinates.
 */
export const validateTicketPrice = (
  creatorTier: UserTier,
  price: number,
  parentPrice?: number,
  _isSettingForSubordinate?: boolean
): boolean => {
  // Company creators can set any price from 0.00 to 1.00
  const min = (creatorTier === UserTier.COMPANY || creatorTier === UserTier.MASTER)
    ? 0.00
    : (parentPrice ?? 0.01);
  const max = 1.00;
  return price >= min && price <= max;
};

/**
 * Gets the allowed ticket price range when creating a subordinate.
 * Company creators: 0.00 – 1.00 (full range).
 * Agent creators: own ticket price – 1.00 (can only set equal or higher).
 */
export const getTicketPriceRange = (
  creatorTier: UserTier,
  parentPrice?: number,
  _isSettingForSubordinate?: boolean
): { min: number; max: number } => {
  // Company/Master can assign any ticket price from 0.00 to 1.00
  if (creatorTier === UserTier.COMPANY || creatorTier === UserTier.MASTER) {
    return { min: 0.00, max: 1.00 };
  }
  // Agent: min = their own ticket price (can set same or higher), max = 1.00
  const min = parentPrice ?? 0.01;
  return { min, max: 1.00 };
};

/**
 * Gets the allowed fight percentage range a setter can assign to a new subordinate.
 * The max a setter can give = their own fight% minus 5 (keeping 5% for themselves).
 * Company has a fixed fight% of 100%, so max they can give agents = 95%.
 * Capped at 90% as the absolute maximum assignable value.
 * Steps of 5% only.
 */
export const getFightPercentageRange = (
  _tier: UserTier,
  setterFightPercentage?: number,
  _isSettingForSubordinate?: boolean,
  setterFightPctLimit?: number
): { min: number; max: number } => {
  // Use fightPctLimit if provided, otherwise fall back to setterFightPercentage
  const ceilingPct = setterFightPctLimit ?? setterFightPercentage ?? 100;
  // max assignable to subordinate = setter's limit - 5, floored at 0
  const max = Math.max(0, ceilingPct - 5);
  return { min: 0, max };
};

/**
 * Constants for business logic
 */
export const MIN_TICKET_PRICE = 0.0;
export const MIN_BET_AMOUNT = 1.0;
